import React, { useState } from 'react';
import './MediaDisplay.css';

export interface MediaItem {
  media_id: string;
  media_type: 'image' | 'table' | 'map' | 'diagram' | 'graph';
  media_format?: string;
  media_url: string;
  caption?: string;
  is_primary?: boolean;
  display_order?: number;
  relationship_type?: string;
  table_data?: any[][];  // For table types
  thumbnail_url?: string;
}

interface MediaDisplayProps {
  media: MediaItem[];
  showCaption?: boolean;
  maxHeight?: number;
  allowZoom?: boolean;
}

/**
 * Component to display media items (images, diagrams, maps, tables) for questions.
 * Supports zooming on click and captions.
 */
const MediaDisplay: React.FC<MediaDisplayProps> = ({
  media,
  showCaption = true,
  maxHeight = 300,
  allowZoom = true
}) => {
  const [zoomedMedia, setZoomedMedia] = useState<MediaItem | null>(null);
  const [imageError, setImageError] = useState<Set<string>>(new Set());

  if (!media || media.length === 0) {
    return null;
  }

  const handleImageError = (mediaId: string) => {
    setImageError(prev => new Set(prev).add(mediaId));
  };

  const getMediaTypeIcon = (type: string) => {
    switch (type) {
      case 'map': return '🗺️';
      case 'diagram': return '📊';
      case 'graph': return '📈';
      case 'table': return '📋';
      default: return '🖼️';
    }
  };

  const renderTableData = (tableData: any[][]) => {
    if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
      return <p className="media-error">Table data not available</p>;
    }

    return (
      <div className="media-table-wrapper">
        <table className="media-table">
          <thead>
            <tr>
              {tableData[0].map((cell: any, idx: number) => (
                <th key={idx}>{cell}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.slice(1).map((row: any[], rowIdx: number) => (
              <tr key={rowIdx}>
                {row.map((cell: any, cellIdx: number) => (
                  <td key={cellIdx}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderMediaItem = (item: MediaItem) => {
    const hasError = imageError.has(item.media_id);

    if (item.media_type === 'table' && item.table_data) {
      return (
        <div key={item.media_id} className="media-item media-item-table">
          {showCaption && item.caption && (
            <div className="media-caption">
              {getMediaTypeIcon(item.media_type)} {item.caption}
            </div>
          )}
          {renderTableData(item.table_data)}
        </div>
      );
    }

    // For images, diagrams, maps, graphs
    if (hasError) {
      return (
        <div key={item.media_id} className="media-item media-item-error">
          <div className="media-error-content">
            <span className="media-error-icon">⚠️</span>
            <span>Image could not be loaded</span>
          </div>
          {showCaption && item.caption && (
            <div className="media-caption">{item.caption}</div>
          )}
        </div>
      );
    }

    return (
      <div
        key={item.media_id}
        className={`media-item media-item-${item.media_type}`}
      >
        {item.is_primary && (
          <span className="media-primary-badge">Primary Figure</span>
        )}
        <img
          src={item.media_url}
          alt={item.caption || `${item.media_type} for question`}
          className="media-image"
          style={{ maxHeight: `${maxHeight}px` }}
          onError={() => handleImageError(item.media_id)}
          loading="lazy"
        />
        {showCaption && item.caption && (
          <div className="media-caption">
            {getMediaTypeIcon(item.media_type)} {item.caption}
          </div>
        )}
        {allowZoom && (
          <button
            type="button"
            className="media-zoom-button"
            onClick={() => setZoomedMedia(item)}
            aria-label="Zoom image"
          >
            🔍 Tap to enlarge
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={`media-display ${media.length > 1 ? 'multi-media' : 'single-media'}`}>
        {media.map(item => renderMediaItem(item))}
      </div>

      {/* Zoom Modal */}
      {zoomedMedia && (
        <div className="media-zoom-overlay" onClick={() => setZoomedMedia(null)}>
          <div className="media-zoom-container">
            <button className="media-zoom-close" onClick={() => setZoomedMedia(null)}>
              ✕
            </button>
            <img
              src={zoomedMedia.media_url}
              alt={zoomedMedia.caption || 'Zoomed image'}
              className="media-zoom-image"
            />
            {zoomedMedia.caption && (
              <div className="media-zoom-caption">{zoomedMedia.caption}</div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MediaDisplay;
